import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GenerationJob, Product, PromptPreset, ContentType, CreateDramaTreatmentInput } from '@muidsoft/shared';
import { api } from '../lib/api.js';
import { useJobPolling } from '../lib/useJobPolling.js';
import { getSelectedProductId, setSelectedProductId } from '../lib/local.js';
import { ProductSheet } from '../components/ProductSheet.js';
import { JobStatusCard } from '../components/JobStatusCard.js';
import {
  IconSparkle,
  IconLightbulb,
  IconCheckCircle,
  IconRefresh,
  IconHourglass,
} from '../components/Icons.js';

// ---------------------------------------------------------------------------
// Helpers CTA
// ---------------------------------------------------------------------------

function CtaIcon({ job }: { job: GenerationJob | null }) {
  if (!job) return <IconSparkle size={18} />;
  switch (job.currentStep) {
    case 'concepts_ready':    return <IconLightbulb size={18} />;
    case 'awaiting_approval': return <IconCheckCircle size={18} />;
    case 'failed':            return <IconRefresh size={18} />;
    case 'completed':         return <IconSparkle size={18} />;
    default:                  return <IconHourglass size={18} />;
  }
}

function ctaLabel(job: GenerationJob | null, starting: boolean): string {
  if (starting) return 'Memulai...';
  if (!job) return 'Generate Prompts';
  switch (job.currentStep) {
    case 'concepts_ready':    return 'Pilih Konsep';
    case 'awaiting_approval': return 'Review Storyboard';
    case 'failed':            return 'Coba Generate Lagi';
    case 'completed':         return 'Generate Prompts Baru';
    default:                  return 'Sedang Diproses...';
  }
}

function isCTADisabled(job: GenerationJob | null, selectedProductId: string | null, starting: boolean): boolean {
  if (!selectedProductId || starting) return true;
  if (!job) return false;

  const INTERACTIVE = new Set<GenerationJob['currentStep']>([
    'concepts_ready',
    'awaiting_approval',
    'failed',
    'completed',
  ]);
  return !INTERACTIVE.has(job.currentStep);
}

// ---------------------------------------------------------------------------
// Drama Genre options
// ---------------------------------------------------------------------------

const DRAMA_GENRES = [
  { value: 'inspirational', label: '✨ Inspiratif' },
  { value: 'family',        label: '👨‍👩‍👧 Keluarga' },
  { value: 'romance',       label: '💕 Romansa' },
  { value: 'thriller',      label: '😰 Thriller' },
  { value: 'social',        label: '🌍 Sosial' },
  { value: 'comedy_drama',  label: '😄 Komedi Drama' },
  { value: 'tragedy',       label: '💔 Tragedi' },
] as const;

const CHARACTER_ROLES = [
  { value: 'protagonist', label: 'Protagonis' },
  { value: 'antagonist',  label: 'Antagonis' },
  { value: 'supporting',  label: 'Pendukung' },
  { value: 'narrator',    label: 'Narator' },
] as const;

// ---------------------------------------------------------------------------
// Drama Treatment Form
// ---------------------------------------------------------------------------

type DramaFormData = {
  title: string;
  genre: CreateDramaTreatmentInput['genre'];
  synopsis: string;
  characters: Array<{ name: string; role: CreateDramaTreatmentInput['characters'][0]['role']; description: string }>;
  conflict: string;
  storyArc: { opening: string; risingAction: string; climax: string; resolution: string };
  setting: string;
  theme: string;
  moodTone: string;
};

function defaultDramaForm(): DramaFormData {
  return {
    title: '',
    genre: 'inspirational',
    synopsis: '',
    characters: [{ name: '', role: 'protagonist', description: '' }],
    conflict: '',
    storyArc: { opening: '', risingAction: '', climax: '', resolution: '' },
    setting: '',
    theme: '',
    moodTone: '',
  };
}

function DramaTreatmentForm({
  value,
  onChange,
}: {
  value: DramaFormData;
  onChange: (v: DramaFormData) => void;
}) {
  function update(patch: Partial<DramaFormData>) {
    onChange({ ...value, ...patch });
  }

  function updateArc(patch: Partial<DramaFormData['storyArc']>) {
    onChange({ ...value, storyArc: { ...value.storyArc, ...patch } });
  }

  function addCharacter() {
    if (value.characters.length >= 5) return;
    onChange({ ...value, characters: [...value.characters, { name: '', role: 'supporting', description: '' }] });
  }

  function removeCharacter(i: number) {
    if (value.characters.length <= 1) return;
    onChange({ ...value, characters: value.characters.filter((_, idx) => idx !== i) });
  }

  function updateCharacter(i: number, patch: Partial<DramaFormData['characters'][0]>) {
    const updated = value.characters.map((c, idx) => idx === i ? { ...c, ...patch } : c);
    onChange({ ...value, characters: updated });
  }

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    borderRadius: 8, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface-raised)', color: 'var(--text-primary)',
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'block', marginBottom: 5,
  };
  const sectionStyle: React.CSSProperties = { marginBottom: 16 };

  return (
    <div>
      {/* Judul + Genre */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>Judul Drama</label>
          <input
            style={fieldStyle}
            placeholder="Judul working title..."
            value={value.title}
            onChange={(e) => update({ title: e.target.value })}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Genre</label>
          <select
            style={fieldStyle}
            value={value.genre}
            onChange={(e) => update({ genre: e.target.value as DramaFormData['genre'] })}
          >
            {DRAMA_GENRES.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Sinopsis */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Sinopsis</label>
        <textarea
          style={{ ...fieldStyle, minHeight: 72, resize: 'vertical' }}
          placeholder="Ringkasan cerita — siapa karakternya, apa yang terjadi, apa konfliknya..."
          value={value.synopsis}
          onChange={(e) => update({ synopsis: e.target.value })}
        />
      </div>

      {/* Karakter */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Karakter ({value.characters.length}/5)</label>
          {value.characters.length < 5 && (
            <button
              onClick={addCharacter}
              style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                background: 'var(--bg-surface-raised)', border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
              }}
            >
              + Tambah
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {value.characters.map((char, i) => (
            <div key={i} style={{
              padding: '10px 12px', borderRadius: 8,
              background: 'var(--bg-surface-raised)', border: '1px solid var(--border-subtle)',
            }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input
                  style={{ ...fieldStyle, flex: 2 }}
                  placeholder="Nama karakter"
                  value={char.name}
                  onChange={(e) => updateCharacter(i, { name: e.target.value })}
                />
                <select
                  style={{ ...fieldStyle, flex: 1 }}
                  value={char.role}
                  onChange={(e) => updateCharacter(i, { role: e.target.value as typeof char.role })}
                >
                  {CHARACTER_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {value.characters.length > 1 && (
                  <button
                    onClick={() => removeCharacter(i)}
                    style={{
                      padding: '4px 8px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--border-subtle)',
                      color: 'var(--status-error)', flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <input
                style={fieldStyle}
                placeholder="Deskripsi singkat — penampilan, kepribadian, latar belakang"
                value={char.description}
                onChange={(e) => updateCharacter(i, { description: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Konflik */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Konflik Utama</label>
        <textarea
          style={{ ...fieldStyle, minHeight: 56, resize: 'vertical' }}
          placeholder="Konflik inti yang menggerakkan cerita — internal atau eksternal..."
          value={value.conflict}
          onChange={(e) => update({ conflict: e.target.value })}
        />
      </div>

      {/* Arc Cerita */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Arc Cerita</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { key: 'opening',      label: '1. Opening', placeholder: 'Setup awal — dunia sebelum konflik' },
            { key: 'risingAction', label: '2. Rising Action', placeholder: 'Eskalasi — konflik memanas' },
            { key: 'climax',       label: '3. Climax', placeholder: 'Puncak — titik balik cerita' },
            { key: 'resolution',   label: '4. Resolution', placeholder: 'Penyelesaian — dunia setelah konflik' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
              <input
                style={fieldStyle}
                placeholder={placeholder}
                value={value.storyArc[key as keyof typeof value.storyArc]}
                onChange={(e) => updateArc({ [key]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Setting + Tema + Mood */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Setting</label>
        <input
          style={fieldStyle}
          placeholder="Lokasi dan era — kapan dan di mana cerita berlangsung"
          value={value.setting}
          onChange={(e) => update({ setting: e.target.value })}
        />
      </div>
      <div style={sectionStyle}>
        <label style={labelStyle}>Tema Utama</label>
        <input
          style={fieldStyle}
          placeholder='Contoh: "keberanian menghadapi kehilangan"'
          value={value.theme}
          onChange={(e) => update({ theme: e.target.value })}
        />
      </div>
      <div style={sectionStyle}>
        <label style={labelStyle}>Mood & Tone</label>
        <input
          style={fieldStyle}
          placeholder='Contoh: "melankolis tapi penuh harapan"'
          value={value.moodTone}
          onChange={(e) => update({ moodTone: e.target.value })}
        />
      </div>
    </div>
  );
}

// Validasi treatment sebelum submit
function validateDramaTreatment(form: DramaFormData): string | null {
  if (!form.title.trim()) return 'Judul drama wajib diisi';
  if (!form.synopsis.trim() || form.synopsis.length < 10) return 'Sinopsis minimal 10 karakter';
  if (form.characters.some((c) => !c.name.trim() || !c.description.trim()))
    return 'Nama dan deskripsi semua karakter wajib diisi';
  if (!form.conflict.trim() || form.conflict.length < 5) return 'Konflik utama wajib diisi';
  if (!form.storyArc.opening.trim()) return 'Opening arc cerita wajib diisi';
  if (!form.storyArc.risingAction.trim()) return 'Rising action arc cerita wajib diisi';
  if (!form.storyArc.climax.trim()) return 'Climax arc cerita wajib diisi';
  if (!form.storyArc.resolution.trim()) return 'Resolution arc cerita wajib diisi';
  if (!form.setting.trim()) return 'Setting wajib diisi';
  if (!form.theme.trim()) return 'Tema utama wajib diisi';
  if (!form.moodTone.trim()) return 'Mood & tone wajib diisi';
  return null;
}

// ---------------------------------------------------------------------------
// StudioPage
// ---------------------------------------------------------------------------

export function StudioPage() {
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [presets, setPresets] = useState<PromptPreset[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(getSelectedProductId());
  const [selectedPresetKey, setSelectedPresetKey] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<'9:16' | '1:1' | '16:9'>('9:16');
  const [durationSeconds, setDurationSeconds] = useState(15);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Content type — 'ad' | 'drama'
  const [contentType, setContentType] = useState<ContentType>('ad');
  const [dramaForm, setDramaForm] = useState<DramaFormData>(defaultDramaForm());

  const { job } = useJobPolling(activeJobId);

  const fetchLatestJob = useCallback(async (productId: string) => {
    try {
      const res = await api.get<{ data: GenerationJob[]; meta: { nextCursor: string | null } }>(
        `/api/bff/jobs?productId=${productId}&limit=1`,
      );
      setActiveJobId(res.data[0]?.id ?? null);
    } catch {
      setActiveJobId(null);
    }
  }, []);

  useEffect(() => {
    api.get<{ data: Product[] }>('/api/bff/products?limit=100').then((res) => {
      setProducts(res.data);
      const savedId = getSelectedProductId();
      const firstId = res.data[0]?.id;
      const effectiveId = savedId ?? firstId ?? null;
      if (effectiveId) {
        setSelectedId(effectiveId);
        setSelectedProductId(effectiveId);
        fetchLatestJob(effectiveId);
      }
    });

    api.get<{ data: PromptPreset[] }>('/api/bff/presets').then((res) => {
      setPresets(res.data);
      if (res.data[0]) setSelectedPresetKey(res.data[0].key);
    });
  }, [fetchLatestJob]);

  const selectedProduct = products.find((p) => p.id === selectedId) ?? null;

  function handleSelectProduct(id: string) {
    setSelectedId(id);
    setSelectedProductId(id);
    setError(null);
    fetchLatestJob(id);
  }

  async function handlePrimaryCta() {
    if (!selectedId) {
      setError('Pilih produk dulu sebelum generate.');
      return;
    }

    if (job) {
      switch (job.currentStep) {
        case 'concepts_ready':
          navigate(`/jobs/${job.id}`);
          return;
        case 'awaiting_approval':
          if (job.storyboardId) {
            navigate(`/storyboard/${job.storyboardId}`);
          } else {
            navigate(`/jobs/${job.id}`);
          }
          return;
        case 'failed':
        case 'completed':
          break;
        default:
          navigate(`/jobs/${job.id}`);
          return;
      }
    }

    // Validasi drama treatment sebelum submit
    if (contentType === 'drama') {
      const validationError = validateDramaTreatment(dramaForm);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setStarting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        productId: selectedId,
        aspectRatio,
        durationSeconds,
        contentType,
      };

      if (contentType === 'ad') {
        body.presetKey = selectedPresetKey ?? undefined;
      } else {
        // Drama: kirim treatment, preset tidak dipakai
        body.dramaTreatment = dramaForm;
      }

      const res = await api.post<{ data: GenerationJob }>('/api/bff/generate', body);
      setActiveJobId(res.data.id);
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memulai generate');
    } finally {
      setStarting(false);
    }
  }

  const ctaDisabled = isCTADisabled(job, selectedId, starting);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Product picker */}
      <button
        className="card"
        style={{ width: '100%', textAlign: 'left', marginBottom: 16, cursor: 'pointer' }}
        onClick={() => setSheetOpen(true)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="text-muted" style={{ fontSize: 12 }}>Produk</div>
            <strong>{selectedProduct?.name ?? 'Pilih produk...'}</strong>
          </div>
          <span aria-hidden style={{ color: 'var(--text-muted)' }}>▾</span>
        </div>
        {selectedProduct && (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {selectedProduct.sellingPoints.length} selling point
          </div>
        )}
      </button>

      {/* ── Tipe Konten ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 15, marginBottom: 8 }}>Tipe Konten</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { value: 'ad' as ContentType,    label: '📣 Iklan Produk', desc: 'Prompt marketing & commercial' },
            { value: 'drama' as ContentType, label: '🎬 Drama',        desc: 'Storytelling sinematik' },
          ].map(({ value, label, desc }) => (
            <button
              key={value}
              onClick={() => { setContentType(value); setError(null); }}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                textAlign: 'left',
                background: contentType === value ? 'var(--bg-surface-raised)' : 'var(--bg-surface)',
                border: `2px solid ${contentType === value ? 'var(--gold-300)' : 'var(--border-subtle)'}`,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Mode Iklan ──────────────────────────────────────────────────── */}
      {contentType === 'ad' && (
        <>
          {presets.length > 0 && (
            <>
              <h2 style={{ fontSize: 15, marginBottom: 8 }}>Preset Kreatif</h2>
              <div className="chip-row" style={{ marginBottom: 20 }}>
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    className={`chip${selectedPresetKey === preset.key ? ' chip--active' : ''}`}
                    onClick={() => setSelectedPresetKey(preset.key)}
                    title={preset.description}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Mode Drama ──────────────────────────────────────────────────── */}
      {contentType === 'drama' && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 12 }}>Treatment Drama</h2>
          <DramaTreatmentForm value={dramaForm} onChange={setDramaForm} />
        </div>
      )}

      {/* Format video — sama untuk keduanya */}
      <div className="field-row">
        <div className="field-group">
          <label>Platform</label>
          <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value as typeof aspectRatio)}>
            <option value="9:16">9:16 (TikTok/Reels)</option>
            <option value="1:1">1:1 (Feed)</option>
            <option value="16:9">16:9 (YouTube)</option>
          </select>
        </div>
        <div className="field-group">
          <label>Durasi</label>
          <select value={durationSeconds} onChange={(e) => setDurationSeconds(Number(e.target.value))}>
            <option value={15}>15 detik</option>
            <option value={30}>30 detik</option>
            <option value={60}>60 detik</option>
            <option value={90}>90 detik</option>
          </select>
        </div>
      </div>
      <p className="text-muted" style={{ fontSize: 12, marginTop: -8, marginBottom: 16 }}>
        Platform &amp; durasi menentukan format prompt yang dihasilkan.
      </p>

      {/* Cara kerja — kontekstual per tipe */}
      <details className="collapsible">
        <summary>Cara kerja {contentType === 'drama' ? 'Drama' : 'Iklan'}</summary>
        {contentType === 'ad' ? (
          <p className="text-muted" style={{ fontSize: 13 }}>
            AI membuat 1 konsep video iklan, lalu kamu pilih. Setelah itu AI menyusun storyboard
            scene-by-scene untuk kamu review. Setelah disetujui, AI menghasilkan prompt siap-pakai
            untuk Midjourney, Sora, Kling, Runway, Veo, dan format generik.
          </p>
        ) : (
          <p className="text-muted" style={{ fontSize: 13 }}>
            AI mengembangkan treatment drama jadi arah kreatif, lalu kamu pilih. Setelah itu AI
            menyusun scene sinematik dengan narasi + dialogue campuran. Output termasuk 6 prompt
            visual (gaya film), screenplay siap produksi, character brief untuk aktor, dan
            scene mood board untuk DOP.
          </p>
        )}
      </details>

      {/* Status job terbaru */}
      {job && (
        <div style={{ marginTop: 20 }}>
          <JobStatusCard job={job} productName={selectedProduct?.name} />
        </div>
      )}

      {/* Error */}
      {error && (
        <p style={{ color: 'var(--status-error)', fontSize: 13, marginTop: 12 }}>{error}</p>
      )}

      {/* CTA sticky */}
      <div className="sticky-cta">
        <button className="btn-primary" onClick={handlePrimaryCta} disabled={ctaDisabled}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <CtaIcon job={starting ? null : job} />
            {ctaLabel(job, starting)}
          </span>
        </button>
      </div>

      {/* Product sheet */}
      {sheetOpen && (
        <ProductSheet
          products={products}
          selectedId={selectedId}
          onSelect={handleSelectProduct}
          onClose={() => setSheetOpen(false)}
          onCreated={(p) => {
            setProducts((prev) => [p, ...prev]);
            handleSelectProduct(p.id);
          }}
          onUpdated={(updatedProduct) => {
            setProducts((prev) =>
              prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p)),
            );
          }}
          onDeleted={(id) => {
            setProducts((prev) => {
              const remaining = prev.filter((p) => p.id !== id);
              if (selectedId === id) {
                const nextId = remaining[0]?.id ?? null;
                setSelectedId(nextId);
                if (nextId) {
                  setSelectedProductId(nextId);
                  fetchLatestJob(nextId);
                } else {
                  setSelectedProductId('');
                  setActiveJobId(null);
                }
              }
              return remaining;
            });
          }}
        />
      )}
    </div>
  );
}
